// Copyright 2023, Durable

import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  ProfileContextType,
  fetchUsersFromProfiles,
  useAuth,
  useProfiles
} from "../components/shared/Auth";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  ClickAwayListener,
  Fade,
  FilledInput,
  Grow,
  IconButton,
  InputAdornment
} from "@mui/material";
import { DurableLogo } from "../components/shared/DurableLogo";
import { ViewUser } from "@durable/durable-client";
import {
  Add as AddIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import { IconForUser } from "../components/shared/UserIcon";
import { onClickwithKeyboard } from "../helpers";
import { logging } from "../logging";

/**
 * The outline/selection box for login options.
 */
function LoginOptionBox({
  children,
  selected,
  onClick
}: {
  children;
  selected: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className={`mb-4 flex cursor-pointer items-center rounded-lg ${
        selected
          ? "bg-coolGrey-900 dark:bg-coolGrey-400"
          : `outline outline-coolGrey-900 hover:bg-coolGrey-900
         dark:outline-coolGrey-400 hover:dark:bg-coolGrey-400`
      }`}
      {...onClickwithKeyboard(() => {
        onClick();
      })}
    >
      {children}
    </div>
  );
}

/**
 * The relevant forms and controls for a user to log in with.
 *
 * @param open - whether this form is open or not.
 * @param setInputUser - Stores the current value in the username field
 * @param sendLogin - Sends the request to log in with the current details
 * @param setInputPass - Stores the current value in the password field
 * @param setInputError - Sets the current input error to display
 * @param setIsRegistering - Sets whether the user is registering
 * @return {React.ReactElement}
 */
// eslint-disable-next-line max-lines-per-function
function LoginForm({
  error,
  usernameRef,
  sendLogin,
  passwordRef,
  users,
  profileContext
}: {
  error?: string;
  usernameRef: MutableRefObject<string>;
  passwordRef: MutableRefObject<string>;
  setInputError: (err: string) => void;
  sendLogin: () => void;
  users: Array<ViewUser>;
  profileContext: ProfileContextType;
}) {
  const [selectedOption, setSelectedOption] = useState<string>(null);
  /**
   * If the user hits enter, submit.
   */
  const onInputEntered = (e) => {
    if (e.key === "Enter" && e.shiftKey === false) {
      sendLogin();
      e.preventDefault();
    }
  };

  /**
   * Password entry with visibility handling
   */
  function PasswordEntry() {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const inputAdornment = (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={() => {
            setShowPassword(!showPassword);
          }}
        >
          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </InputAdornment>
    );

    return (
      <div className="password-entry px-4">
        <FilledInput
          required
          fullWidth
          className="bg-coolGrey-900 dark:bg-coolGrey-400"
          id="login-password"
          onChange={(event) => {
            passwordRef.current = event.currentTarget.value;
          }}
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          onKeyPress={onInputEntered}
          endAdornment={inputAdornment}
          onSubmit={sendLogin}
        />
      </div>
    );
  }

  /**
   * Classic username and password forms, appear after a user selects manual
   * entry.
   */
  function ManualLogin() {
    return (
      <Grow in={true}>
        <div className="h-full w-full items-center justify-center pb-6">
          <div className="username-entry px-4">
            <FilledInput
              required
              fullWidth
              className="bg-coolGrey-900 dark:bg-coolGrey-400"
              error={error ? true : false}
              id="login-email"
              onChange={(event) => {
                usernameRef.current = event.currentTarget.value;
              }}
              placeholder="Username"
              onKeyPress={onInputEntered}
              onSubmit={sendLogin}
            />
          </div>
          <PasswordEntry />
        </div>
      </Grow>
    );
  }

  const isEmail = selectedOption == "email";
  /**
   * Handles the login button press action. If the authentication method is
   * email, it calls the sendLogin function. Otherwise, it sets the current
   * profile in the profile context to the selected option.
   */
  function loginButtonPressed() {
    if (isEmail) {
      sendLogin();
    } else {
      profileContext.setCurrentProfile(selectedOption);
    }
  }
  return (
    <>
      <div className="my-6 select-none">
        <ClickAwayListener onClickAway={() => setSelectedOption(null)}>
          <div>
            {users.map((user) => (
              <LoginOptionBox
                key={`user-${user.id}`}
                selected={selectedOption == user.id}
                onClick={() => setSelectedOption(user.id)}
              >
                <div className="flex items-center p-3">
                  <div className="mr-3 ml-1 h-full items-center py-2">
                    <IconForUser user={user} />
                  </div>
                  <div>
                    <div>{user.username}</div>
                    <div
                      className={`text-sm text-coolGrey-500
                        dark:text-coolGrey-800`}
                    >
                      Sign in with existing user
                    </div>
                  </div>
                </div>
              </LoginOptionBox>
            ))}
            <LoginOptionBox
              selected={isEmail}
              onClick={() => setSelectedOption("email")}
            >
              {isEmail ? (
                <ManualLogin />
              ) : (
                <div className="p-3">
                  <div className="flex items-center">
                    <div className="mr-3 ml-1 h-full items-center py-2">
                      {users.length > 0 ? <AddIcon /> : <EmailIcon />}
                    </div>
                    <div>
                      <div>Username</div>
                      <div
                        className={`text-sm text-coolGrey-500
                        dark:text-coolGrey-800`}
                      >
                        Sign in with username
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </LoginOptionBox>
          </div>
        </ClickAwayListener>
      </div>
      <div>
        <Button
          onClick={loginButtonPressed}
          onTouchEnd={(e) => {
            e.preventDefault(); // Prevent ghost clicks
            loginButtonPressed();
          }}
          fullWidth
          variant="contained"
          disabled={!selectedOption}
        >
          Login
        </Button>
        <div
          className="mt-4 text-center 
        text-xs text-coolGrey-500 dark:text-coolGrey-800 px-4"
        >
          By continuing, you acknowledge that you understand <br /> and agree
          to the{" "}
          <a
            href="https://docs.durable.ai/Terms-and-Conditions-1d524116c0548032aa1ef5352eee6ec2"
            target="_blank"
            className="text-durableTealDark-300 hover:underline"
            rel="noreferrer"
          >
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a
            href="https://docs.durable.ai/Privacy-Policy-1d524116c05480c48d70f1f764f2d4da"
            target="_blank"
            className="text-durableTealDark-300 hover:underline"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </>
  );
}

/**
 * Component to render login page.
 *
 * Uses auth context for token / login / logout handling.
 */
export function LoginPage() {
  // Login form state.
  const usernameRef = useRef();
  const passwordRef = useRef();
  // Auth context hooks.
  const [err, setErr] = useState<string>("");
  const { token, handleLogin, handleTokenLogin } = useAuth();
  const profileContext = useProfiles();
  const navigate = useNavigate();
  const [users, setUsers] = useState<ViewUser[]>([]);
  const [URLParameters] = useSearchParams();

  /**
   *  Login callback for successful login.
   */
  const onLogin = async () => {
    try {
      await handleLogin(usernameRef.current, passwordRef.current);
      navigate("/app/start");
    } catch (error) {
      setErr(error);
    }
  };

  // Check URL for access_token parameter and memoize it
  const accessToken = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("access_token");
  }, []);

  useEffect(() => {
    // Only run this effect once if accessToken exists
    if (!accessToken) return;

    logging.info("Access token found in login page URL");

    // Remove token from URL for security
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("access_token");
    const newUrl =
      window.location.pathname +
      (urlParams.toString() ? "?" + urlParams.toString() : "");
    window.history.replaceState({}, document.title, newUrl);

    const attemptTokenLogin = async () => {
      // Attempt to login with token and navigate to start.
      try {
        await handleTokenLogin(accessToken);
        navigate("/app/start");
      } catch (error) {
        setErr(error);
      }
    };

    attemptTokenLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  /**
   * Load in users asynchronously on load or user profile changes.
   *
   * Profiles that are unauthenticated, don't exist, or otherwise encounter an
   * error are removed from the profiles.
   */
  useEffect(() => {
    fetchUsersFromProfiles(
      Array.from(profileContext.profiles.values()),
      setUsers,
      profileContext.removeProfile,
      null
    );
  }, [profileContext.profiles, profileContext.removeProfile]);

  /**
   * If our token is set in the user auth context, navigate to start. However if
   * an access token is provided then use that to logout the current user and
   * login using the access token.
   */
  if (token !== undefined && token !== null && !accessToken) {
    return (
      <Navigate to={URLParameters.get("returnTo") ?? "/app/start"} replace />
    );
  }

  /**
   * Otherwise, present the login form.
   */
  return (
    <div
      id="login-form"
      className="flex h-screen w-full items-center justify-center"
    >
      <div className="flex flex-col">
        <Fade in={true} unmountOnExit>
          <div className="mb-12 flex w-full justify-center">
            <DurableLogo variant="dynamic" size={250} />
          </div>
        </Fade>
        <Grow in={true} unmountOnExit>
          <Card className="items-center p-10 shadow-2xl dark:bg-coolGrey-300">
            <div>
              <div className="m-3 text-2xl">Sign In</div>
              <div
                className={`m-3 text-sm text-coolGrey-200
              dark:text-coolGrey-900`}
              >
                Welcome back! Sign into your account below.
              </div>
            </div>
            <LoginForm
              error={err}
              usernameRef={usernameRef}
              sendLogin={onLogin}
              passwordRef={passwordRef}
              setInputError={setErr}
              users={users}
              profileContext={profileContext}
            />
          </Card>
        </Grow>
        <div className="flex w-full justify-center">
          <Fade in={true} unmountOnExit>
            <div
              className={`m-3 mt-6 text-sm font-semibold text-coolGrey-400
            dark:text-coolGrey-800`}
            >
              New to Durable?{" "}
              <a
                href="mailto:hi@durable.ai"
                className=" text-durableTealDark-300"
              >
                Request access.
              </a>
            </div>
          </Fade>
        </div>
      </div>
    </div>
  );
}
